/**
 * Streaming Session Service
 * Manages WebSocket connection to the backend for session lifecycle and transcript persistence.
 *
 * Handles:
 * - Connection with Cognito access token
 * - Session lifecycle (start, stop)
 * - Transcript fragment persistence
 * - Reconnection with exponential backoff
 * - Heartbeat to prevent API Gateway idle timeout
 * - Interrupted session recovery via REST API
 */

import { createLogger } from '@/lib/logger';
import { API_URL } from '@/lib/apiConfig';

const log = createLogger('StreamingSessionService');

// In dev, use the Vite proxy path (ws://localhost:3000/ws); in prod, use the full WebSocket URL
const WS_URL = import.meta.env.DEV
  ? `ws://${typeof window !== 'undefined' ? window.location.host : 'localhost:3000'}/ws`
  : (import.meta.env.REACT_APP_STREAMING_WS_URL || '');
const ADD_API_URL = API_URL;
const HEARTBEAT_INTERVAL_MS = 8 * 60 * 1000; // 8 minutes
const MAX_RECONNECT_ATTEMPTS = 4;
const RECONNECT_BASE_DELAY_MS = 1000;

export interface StreamingSession {
  sessionId: string;
  status: string;
  languageCode: string;
  startedAt: number;
  lastActiveAt: number;
  durationSeconds: number;
  fragmentCount: number;
  hasTranscript: boolean;
}

export interface SessionStartedMessage {
  type: 'session.started';
  sessionId: string;
}

export interface SessionResumedMessage {
  type: 'session.resumed';
  sessionId: string;
  existingTranscript: string;
  fragmentCount: number;
  durationSeconds: number;
}

export interface SessionStoppedMessage {
  type: 'session.stopped';
  sessionId: string;
  jobId?: string;
  error?: string;
}

export interface SessionTimeoutMessage {
  type: 'session.timeout';
  sessionId: string;
  message: string;
  maxDurationSeconds: number;
}

export interface ErrorMessage {
  type: 'error';
  message: string;
}

export type WebSocketMessage =
  | SessionStartedMessage
  | SessionResumedMessage
  | SessionStoppedMessage
  | SessionTimeoutMessage
  | ErrorMessage;

export interface StreamingSessionCallbacks {
  onMessage: (message: WebSocketMessage) => void;
  onConnected: () => void;
  onDisconnected: () => void;
  onReconnecting: (attempt: number) => void;
  onReconnectFailed: () => void;
}

export class StreamingSessionManager {
  private ws: WebSocket | null = null;
  private callbacks: StreamingSessionCallbacks;
  private getAccessToken: () => Promise<string | null>;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private pendingMessages: string[] = [];
  private _isConnected = false;

  constructor(
    getAccessToken: () => Promise<string | null>,
    callbacks: StreamingSessionCallbacks
  ) {
    this.getAccessToken = getAccessToken;
    this.callbacks = callbacks;
  }

  get isConnected(): boolean {
    return this._isConnected;
  }

  /**
   * Connect to the WebSocket API.
   */
  async connect(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    if (!WS_URL) {
      log.warn('REACT_APP_STREAMING_WS_URL not configured');
      return;
    }

    this.intentionallyClosed = false;

    try {
      const token = await this.getAccessToken();
      if (!token) {
        log.warn('No access token available for WebSocket connection');
        return;
      }
      const url = `${WS_URL}?token=${encodeURIComponent(token)}`;
      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        log.info('WebSocket connected');
        this._isConnected = true;
        this.reconnectAttempts = 0;
        this.startHeartbeat();
        this.callbacks.onConnected();

        // Flush pending messages
        while (this.pendingMessages.length > 0) {
          const msg = this.pendingMessages.shift()!;
          this.ws?.send(msg);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketMessage;
          this.callbacks.onMessage(data);
        } catch (err) {
          log.warn('Failed to parse WebSocket message', { error: String(err) });
        }
      };

      this.ws.onclose = () => {
        log.info('WebSocket closed');
        this._isConnected = false;
        this.stopHeartbeat();
        this.callbacks.onDisconnected();

        if (!this.intentionallyClosed) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (event) => {
        log.warn('WebSocket error', { event });
      };
    } catch (error) {
      log.error('Failed to connect WebSocket', error instanceof Error ? error : new Error(String(error)));
      this.attemptReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket.
   */
  disconnect(): void {
    this.intentionallyClosed = true;
    this.stopHeartbeat();
    this.clearReconnectTimer();
    this.pendingMessages = [];

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this._isConnected = false;
  }

  /**
   * Send a session.start message.
   */
  startSession(languageCode: string, resumeSessionId?: string): void {
    const payload: Record<string, string> = {
      action: 'session.start',
      languageCode,
    };
    if (resumeSessionId) {
      payload.resumeSessionId = resumeSessionId;
    }
    this.send(JSON.stringify(payload));
  }

  /**
   * Send a transcript fragment.
   */
  sendTranscriptFragment(
    fragment: { index: number; text: string; startTime: number; endTime: number; isFinal: boolean },
    durationSeconds: number
  ): void {
    this.send(JSON.stringify({
      action: 'session.transcript',
      fragment,
      durationSeconds,
    }));
  }

  /**
   * Send a session.stop message with project/template info.
   */
  stopSession(params: {
    projectId: string;
    projectName: string;
    templateId: string;
    templateName: string;
    outputFormat: string;
    identityId: string;
    audioS3Key?: string;
    audioChunkKeys?: string[];
    transcriptS3Key?: string;
  }): void {
    this.send(JSON.stringify({
      action: 'session.stop',
      ...params,
    }));
  }

  /**
   * Get interrupted/active sessions via REST API.
   */
  async getInterruptedSessions(accessToken: string): Promise<StreamingSession[]> {
    if (!ADD_API_URL) return [];

    try {
      const response = await fetch(`${ADD_API_URL}/streaming/sessions`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) return [];

      const data = await response.json();
      return data.sessions || [];
    } catch (error) {
      log.warn('Failed to fetch interrupted sessions', { error: String(error) });
      return [];
    }
  }

  // -- Private methods --

  private send(message: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(message);
    } else {
      // Buffer for reconnection
      this.pendingMessages.push(message);
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send(JSON.stringify({ action: 'heartbeat' }));
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      log.warn('Max reconnect attempts reached');
      this.callbacks.onReconnectFailed();
      return;
    }

    this.reconnectAttempts++;
    const delay = RECONNECT_BASE_DELAY_MS * Math.pow(2, this.reconnectAttempts - 1);

    log.info(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.callbacks.onReconnecting(this.reconnectAttempts);

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }
}
